import type { DivExtensionClass } from '@divkitframework/divkit/typings/common.d.ts';
import { appConfig } from '@/config/env';
import {
  ensureFunnelUser,
  funnelUser,
  getFunnelAnalytics,
  initFunnelAnalytics,
  type FunnelAnalytics,
} from '@/modules/analytics/runtime';
import { loadLayoutExtensions, type ExtensionRuntimeOptions } from '@/modules/extensions/registry';
import { createTranslations, type FunnelTranslations } from '@/modules/i18n';
import { createLogger, type Logger } from '@/modules/logger';
import type { FunnelUser } from '@/modules/quiz/user';

/** Per-layout inputs: the shared translations, logger and language are filled in by the runtime. */
export type LayoutExtensionOptions = Omit<ExtensionRuntimeOptions, 'translations' | 'logger' | 'language'>;

/**
 * Services every route used to build for itself. One instance per app run: the quiz, the paywall and the
 * post-payment screens share the logger, the translations (one i18next, locales fetched once) and the
 * extension loader instead of recreating them on each route mount.
 *
 * Analytics is not owned here — `modules/analytics/runtime` already keeps the single sign-in and the single
 * `init()`; this is a reference to it, so the two never disagree about the instance.
 */
export interface FunnelRuntime {
  readonly language: string;
  readonly logger: Logger;
  readonly translations: FunnelTranslations;
  /** Resolves the layout contract's extensions; memoised per id set inside the registry. */
  extensions(ids: string[], options?: LayoutExtensionOptions): Promise<Map<string, DivExtensionClass>>;
  readonly analytics: {
    user(): FunnelUser;
    ensureUser(): Promise<FunnelUser>;
    get(): FunnelAnalytics;
    init(extra?: Record<string, unknown>): FunnelAnalytics;
  };
}

let runtime: FunnelRuntime | undefined;

export const createFunnelRuntime = (language = appConfig.lang): FunnelRuntime => {
  const logger = createLogger(appConfig);
  const translations = createTranslations(language);
  return {
    language,
    logger,
    translations,
    extensions: (ids, options = {}) => loadLayoutExtensions(ids, { ...options, translations, logger, language }),
    analytics: {
      user: funnelUser,
      ensureUser: ensureFunnelUser,
      get: getFunnelAnalytics,
      init: initFunnelAnalytics,
    },
  };
};

/** The app-wide instance; `RuntimeProvider` hands it down, pages without one fall back to it directly. */
export const getFunnelRuntime = (): FunnelRuntime => (runtime ??= createFunnelRuntime());

/** Tests only. Analytics has its own reset — this one leaves it alone on purpose. */
export const resetFunnelRuntime = (): void => {
  runtime = undefined;
};
